import { toParameterObject, toParameterClass } from 'app/model/adapters/linkedservices/parameter-config.util';
import { AzureDataBricksLinkedService } from './../../linkedservice/azure-data-bricks.linkedservice';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from './azure-key-vault.util';
import { ValueAdapter } from '../value.adapter';
import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { databricksInitScriptsToResource, databricksInitScriptsToArrayModel } from 'app/model/pipeline/databricks-parameter.util';
import { GlobalVariables } from 'app/services/globals';
import { AuthenticationReferenceType, CredentialTypeOptions, Credential, ParameterConfig } from 'shell-models-lib';
import { DatabricksParameter } from 'app/model/pipeline/databricks-parameter';
import { ReferenceAdapter } from '../reference.adapter';
import { IDockerContainerSettings } from 'app/model/linkedservice/azure-data-bricks.linkedservice';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';

enum SupportedAuthTypes {
    MSI = 'MSI'
}

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureDataBricksLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                domain: '*',
                authentication: '*',
                accessToken: 'secret',
                workspaceResourceId: '*',
                existingClusterId: '*',
                instancePoolId: '*',
                encryptedCredential: '*',
                newClusterVersion: '*',
                newClusterNumOfWorker: '*',
                newClusterNodeType: '*',
                newClusterSparkConf: '*',
                newClusterSparkEnvVars: '*',
                newClusterCustomTags: '*',
                newClusterLogDestinationSettings: '*',
                newClusterLogDestination: '*',
                newClusterInitScripts: '*',
                newClusterDriverNodeType: '*',
                clusterOption: '*',
                dataSecurityMode: '*'
            },
            parameters: '*'
        }
    }
})
export class AzureDataBricksLinkedServiceAdapter extends LinkedServiceAdapter<AzureDataBricksLinkedService> {
    protected supportFirstClassParameterExperience = true;

    /**
     * Update AuthenticationType based on fetched credential type
     * @param model linked service model
     */
    public async postToModel(model: AzureDataBricksLinkedService): Promise<void> {
        let finalAuthType: AuthenticationType = model.authenticationType;
        if (model.credential) {
            const credType = model.credential.getType();
            if (credType === CredentialTypeOptions.ServicePrincipal) {
                finalAuthType = AuthenticationType.ServicePrincipal;
            } else if (credType === CredentialTypeOptions.ManagedIdentity) {
                finalAuthType = AuthenticationType.ManagedServiceIdentity;
            } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                finalAuthType = AuthenticationType.UserAssignedManagedIdentity;
            } else {
                finalAuthType = model.authenticationType;
            }
        }
        model.authenticationType = finalAuthType;
    }

    protected modelConstructor(): new () => AzureDataBricksLinkedService {
        return AzureDataBricksLinkedService;
    }

    protected model(): IModel {
        return AzureDataBricksLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureDataBricksLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureDatabricksLinkedService;

        properties.type = Metamodel.modelFor(AzureDataBricksLinkedService).name;
        properties.typeProperties = {
            domain: this.valueToResource(linkedService, AzureDataBricksLinkedService.DOMAIN)
        };
        if (linkedService.authenticationType === AuthenticationType.ManagedServiceIdentity || linkedService.authenticationType === AuthenticationType.UserAssignedManagedIdentity) {
            properties.typeProperties.authentication = SupportedAuthTypes.MSI;
            properties.typeProperties.workspaceResourceId = this.valueToResource(linkedService, AzureDataBricksLinkedService.WORKSPACE_RESOURCE_ID);
            if (linkedService.credential &&
                ((linkedService.authenticationType === AuthenticationType.ManagedServiceIdentity && linkedService.authenticationReferenceType === AuthenticationReferenceType.Credential) ||
                    linkedService.authenticationType === AuthenticationType.UserAssignedManagedIdentity)) {
                properties.typeProperties.credential = ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference;
            }
        } else {
            properties.typeProperties.accessToken = passwordOrAKVtoSecretBase(linkedService.accessToken, this.context);
        }
        if (linkedService.useClusterMode === 'new') {
            properties.typeProperties.newClusterNodeType = this.valueToResource(linkedService, AzureDataBricksLinkedService.NODE_TYPE);
            properties.typeProperties.newClusterNumOfWorker = this.valueToResource(linkedService, AzureDataBricksLinkedService.NUM_WORKER);
            properties.typeProperties.newClusterSparkConf = toParameterObject(linkedService.newClusterSparkConf);
            properties.typeProperties.newClusterSparkEnvVars = toParameterObject(linkedService.newClusterSparkEnvVars);
            properties.typeProperties.newClusterCustomTags = toParameterObject(linkedService.newClusterCustomTags);
            properties.typeProperties.newClusterVersion = this.valueToResource(linkedService, AzureDataBricksLinkedService.VERSION);
            if (GlobalVariables.enableDatabricksClusterLogVolumes) {
                const newClusterLogDestination = this.valueToResource(linkedService, AzureDataBricksLinkedService.DBFS_DESTINATION) as string;
                const destinationType = this.toClusterLogDestinationTypeResource(
                    this.valueToResource(linkedService, AzureDataBricksLinkedService.DESTINATION_TYPE) as string
                );
                if (!stringIsNullOrEmpty(newClusterLogDestination) && !stringIsNullOrEmpty(destinationType)) {
                    properties.typeProperties.newClusterLogDestinationSettings = {
                        type: destinationType,
                        path: newClusterLogDestination
                    };
                }
            } else {
                properties.typeProperties.newClusterLogDestination = this.valueToResource(linkedService, AzureDataBricksLinkedService.DBFS_DESTINATION);
            }
            properties.typeProperties.newClusterDriverNodeType = this.valueToResource(linkedService, AzureDataBricksLinkedService.DRIVER_NODE_TYPE);
            properties.typeProperties.clusterOption = this.valueToResource(linkedService, AzureDataBricksLinkedService.CLUSTER_OPTION);
            properties.typeProperties.newClusterInitScripts = this.getInitScriptsToResource(linkedService);
            properties.typeProperties.dataSecurityMode = this.valueToResource(linkedService, AzureDataBricksLinkedService.DATA_SECURITY_MODE);
            if (GlobalVariables.enableNewClusterAttributes) {
                const policyId = this.valueToResource(linkedService, AzureDataBricksLinkedService.POLICY_ID);
                if (!stringIsNullOrEmpty(policyId)) {
                    properties.typeProperties.policyId = policyId;
                }

                if (linkedService.newClusterAttributes.availability === 'SpotWithFallback') {
                    properties.typeProperties.newClusterAttributes = this.valueToResource(linkedService, AzureDataBricksLinkedService.ADDITIONAL_CLUSTER_ATTRIBUTES);
                }
                properties.typeProperties.dockerImage = this.convertDockerSettingsToResource(linkedService);

            }
        } else if (linkedService.useClusterMode === 'exist') {
            properties.typeProperties.existingClusterId = this.valueToResource(linkedService, AzureDataBricksLinkedService.EXISTING_CLUSTER_ID);
        } else if (linkedService.useClusterMode === 'pool') {
            properties.typeProperties.instancePoolId = this.valueToResource(linkedService, AzureDataBricksLinkedService.INSTANCE_POOL_ID);
            properties.typeProperties.newClusterNodeType = this.valueToResource(linkedService, AzureDataBricksLinkedService.NODE_TYPE);
            properties.typeProperties.newClusterNumOfWorker = this.valueToResource(linkedService, AzureDataBricksLinkedService.NUM_WORKER);
            properties.typeProperties.newClusterSparkConf = toParameterObject(linkedService.newClusterSparkConf);
            properties.typeProperties.newClusterSparkEnvVars = toParameterObject(linkedService.newClusterSparkEnvVars);
            properties.typeProperties.newClusterCustomTags = toParameterObject(linkedService.newClusterCustomTags);
            properties.typeProperties.newClusterVersion = this.valueToResource(linkedService, AzureDataBricksLinkedService.VERSION);
            if (GlobalVariables.enableDatabricksClusterLogVolumes) {
                const newClusterLogDestination = this.valueToResource(linkedService, AzureDataBricksLinkedService.DBFS_DESTINATION) as string;
                const destinationType = this.toClusterLogDestinationTypeResource(
                    this.valueToResource(linkedService, AzureDataBricksLinkedService.DESTINATION_TYPE) as string
                );
                if (!stringIsNullOrEmpty(newClusterLogDestination) && !stringIsNullOrEmpty(destinationType)) {
                    properties.typeProperties.newClusterLogDestinationSettings = {
                        type: destinationType,
                        path: newClusterLogDestination
                    };
                }
            } else {
                properties.typeProperties.newClusterLogDestination = this.valueToResource(linkedService, AzureDataBricksLinkedService.DBFS_DESTINATION);
            }
            properties.typeProperties.newClusterInitScripts = this.getInitScriptsToResource(linkedService);
            properties.typeProperties.clusterOption = this.valueToResource(linkedService, AzureDataBricksLinkedService.CLUSTER_OPTION);

            if (GlobalVariables.enableNewClusterAttributes) {
                const policyId = this.valueToResource(linkedService, AzureDataBricksLinkedService.POLICY_ID);
                if (!stringIsNullOrEmpty(policyId)) {
                    properties.typeProperties.policyId = policyId;
                }

                if (linkedService.newClusterAttributes.availability === 'SpotWithFallback') {
                    properties.typeProperties.newClusterAttributes = this.valueToResource(linkedService, AzureDataBricksLinkedService.ADDITIONAL_CLUSTER_ATTRIBUTES);
                }
                properties.typeProperties.dockerImage = this.convertDockerSettingsToResource(linkedService);
            }
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureDataBricksLinkedService): Promise<AzureDataBricksLinkedService> {
        const prop = linkedServiceResource.properties as Model.AzureDatabricksLinkedService;
        ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.DOMAIN, prop.typeProperties.domain);

        if (prop.typeProperties && prop.typeProperties.authentication && prop.typeProperties.authentication === SupportedAuthTypes.MSI) {
            // set correct auth method in postToModel call if credential reference is present.
            linkedService.authenticationType = AuthenticationType.ManagedServiceIdentity;

            ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.WORKSPACE_RESOURCE_ID, prop.typeProperties.workspaceResourceId);
            if (prop.typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), prop.typeProperties.credential, this.context);
                linkedService.authenticationReferenceType = AuthenticationReferenceType.Credential;
            }
        } else {
            linkedService.accessToken = secretBaseToPasswordOrAKV(prop.typeProperties.accessToken, true, this.context);
        }
        if (prop.typeProperties.existingClusterId) {
            linkedService.useClusterMode = 'exist';
            ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.EXISTING_CLUSTER_ID, prop.typeProperties.existingClusterId);
        } else if (prop.typeProperties.instancePoolId) {
            linkedService.useClusterMode = 'pool';
            ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.INSTANCE_POOL_ID, prop.typeProperties.instancePoolId);
            ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.NODE_TYPE, prop.typeProperties.newClusterNodeType);
            ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.NUM_WORKER, prop.typeProperties.newClusterNumOfWorker);
            linkedService.newClusterSparkConf = toParameterClass(prop.typeProperties.newClusterSparkConf, linkedService);
            linkedService.newClusterSparkEnvVars = toParameterClass(prop.typeProperties.newClusterSparkEnvVars, linkedService);
            linkedService.newClusterCustomTags = toParameterClass(prop.typeProperties.newClusterCustomTags, linkedService);
            ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.VERSION, prop.typeProperties.newClusterVersion);
            ValueAdapter.toModel(
                linkedService,
                AzureDataBricksLinkedService.DESTINATION_TYPE,
                this.toClusterLogDestinationTypeModel(prop.typeProperties.newClusterLogDestinationSettings?.type)
            );
            ValueAdapter.toModel(
                linkedService,
                AzureDataBricksLinkedService.DBFS_DESTINATION,
                prop.typeProperties.newClusterLogDestinationSettings?.path ?? prop.typeProperties.newClusterLogDestination
            );
            linkedService.newClusterInitScripts = this.getInitScriptsToModel(prop, linkedService);
            ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.CLUSTER_OPTION, prop.typeProperties.clusterOption);

            if (GlobalVariables.enableNewClusterAttributes) {
                ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.ADDITIONAL_CLUSTER_ATTRIBUTES, prop.typeProperties.newClusterAttributes);
                ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.POLICY_ID, prop.typeProperties.policyId);

                const dockerModelConfig: IDockerContainerSettings = this.convertDockerResourceToModel(linkedServiceResource);
                ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.DOCKER_CONFIGURATION, dockerModelConfig);
            }
        } else if (prop.typeProperties.newClusterNodeType) {
            linkedService.useClusterMode = 'new';
            ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.NODE_TYPE, prop.typeProperties.newClusterNodeType);
            ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.NUM_WORKER, prop.typeProperties.newClusterNumOfWorker);
            linkedService.newClusterSparkConf = toParameterClass(prop.typeProperties.newClusterSparkConf, linkedService);
            linkedService.newClusterSparkEnvVars = toParameterClass(prop.typeProperties.newClusterSparkEnvVars, linkedService);
            linkedService.newClusterCustomTags = toParameterClass(prop.typeProperties.newClusterCustomTags, linkedService);
            ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.VERSION, prop.typeProperties.newClusterVersion);
            ValueAdapter.toModel(
                linkedService,
                AzureDataBricksLinkedService.DESTINATION_TYPE,
                this.toClusterLogDestinationTypeModel(prop.typeProperties.newClusterLogDestinationSettings?.type)
            );
            ValueAdapter.toModel(
                linkedService,
                AzureDataBricksLinkedService.DBFS_DESTINATION,
                prop.typeProperties.newClusterLogDestinationSettings?.path ?? prop.typeProperties.newClusterLogDestination
            );
            linkedService.newClusterInitScripts = this.getInitScriptsToModel(prop, linkedService);
            ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.DRIVER_NODE_TYPE, prop.typeProperties.newClusterDriverNodeType);
            ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.CLUSTER_OPTION, prop.typeProperties.clusterOption);
            ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.DATA_SECURITY_MODE, prop.typeProperties.dataSecurityMode);

            if (GlobalVariables.enableNewClusterAttributes) {
                ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.ADDITIONAL_CLUSTER_ATTRIBUTES, prop.typeProperties.newClusterAttributes);
                ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.POLICY_ID, prop.typeProperties.policyId);

                const dockerModelConfig: IDockerContainerSettings = this.convertDockerResourceToModel(linkedServiceResource);
                ValueAdapter.toModel(linkedService, AzureDataBricksLinkedService.DOCKER_CONFIGURATION, dockerModelConfig);
            }
        } else {
            linkedService.useClusterMode = 'serverless';
        }
        return linkedService;
    }

    private getInitScriptsToResource(linkedService: AzureDataBricksLinkedService): ParameterConfig[] | DatabricksParameter[] {
        return databricksInitScriptsToResource(linkedService.newClusterInitScripts);
    }

    private toClusterLogDestinationTypeResource(value: string | undefined): Model.IDatabricksClusterLogDestinationType | undefined {
        if (stringIsNullOrEmpty(value)) {
            return undefined;
        }

        return value.toLowerCase();
    }

    private toClusterLogDestinationTypeModel(value: Model.IDatabricksClusterLogDestinationType | undefined): string | undefined {
        if (!value) {
            return undefined;
        }

        switch (value.toLowerCase()) {
            case 'dbfs':
                return 'Dbfs';
            case 'volumes':
                return 'Volumes';
            case 'abfs':
                return 'Abfs';
            case 'abfss':
                return 'Abfss';
            default:
                return value;
        }
    }

    private getInitScriptsToModel(properties: Model.AzureDatabricksLinkedService, linkedService: AzureDataBricksLinkedService): ParameterConfig[] | DatabricksParameter[] {
        return databricksInitScriptsToArrayModel(properties.typeProperties.newClusterInitScripts, linkedService);
    }

    private convertDockerSettingsToResource(linkedService: AzureDataBricksLinkedService): Model.IDockerConfiguration {
        const dockerConfig = this.valueToResource(linkedService, AzureDataBricksLinkedService.DOCKER_CONFIGURATION) as IDockerContainerSettings;
        if (dockerConfig) {
            const auth = dockerConfig.useBasicAuth ? {
                username: dockerConfig.username,
                password: passwordOrAKVtoSecretBase(dockerConfig.password, this.context)
            } : undefined;

            return {
                url: dockerConfig.containerUrl,
                basicAuth: auth
            };
        }
        return undefined;
    }

    private convertDockerResourceToModel(linkedServiceResource: Model.LinkedServiceResource): IDockerContainerSettings {
        const prop = linkedServiceResource.properties as Model.AzureDatabricksLinkedService;
        const dockerConfig: Model.IDockerConfiguration = prop.typeProperties.dockerImage;

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        let dockerModelConfig = {} as IDockerContainerSettings;
        if (dockerConfig) {
            dockerModelConfig.containerUrl = dockerConfig.url;
            if (dockerConfig.basicAuth) {
                dockerModelConfig.useBasicAuth = true;
                dockerModelConfig.username = dockerConfig.basicAuth.username;
                dockerModelConfig.password = secretBaseToPasswordOrAKV(dockerConfig.basicAuth.password, true, this.context) as string;
            } else {
                dockerModelConfig.useBasicAuth = false;
                dockerModelConfig.username = undefined;
                dockerModelConfig.password = undefined;
            }
        } else {
            dockerModelConfig = undefined;
        }

        return dockerModelConfig;
    }
}
